import type { BaselineKind, BaselineSnapshot, UserProfile } from '../types';

/** Bump when afskrift-baseline skal tages om (fx tastatur-fix for é). */
export const BASELINE_TRANSCRIPTION_SCHEMA = 2;

export function needsOnboarding(user: UserProfile): boolean {
  return !user.onboardingCompletedAt;
}

/** Onboarding færdig, men mangler gyldig afskrift-baseline. */
export function needsBaselineTranscription(user: UserProfile): boolean {
  return !!user.onboardingCompletedAt && !user.baselineTranscription;
}

/**
 * Nulstil afskrift-baseline én gang per schema-version (fx efter é/Option-fix).
 * Hverdagstest og mål beholdes.
 */
export function migrateBaselineTranscription(user: UserProfile): UserProfile {
  const version = user.baselineTranscriptionSchemaVersion ?? 1;
  if (version >= BASELINE_TRANSCRIPTION_SCHEMA) return user;
  return {
    ...user,
    baselineTranscriptionSchemaVersion: BASELINE_TRANSCRIPTION_SCHEMA,
    baselineTranscription: undefined,
  };
}

export function normalizeBaselineSnapshot(
  raw: BaselineSnapshot | undefined,
  kind: BaselineKind,
): BaselineSnapshot | undefined {
  if (!raw || raw.kind !== kind) return undefined;
  if (!Number.isFinite(raw.wpm) || !Number.isFinite(raw.accuracy)) {
    return undefined;
  }
  return {
    kind,
    wpm: Math.max(0, raw.wpm),
    accuracy: Math.max(0, Math.min(1, raw.accuracy)),
    correctChars: Math.max(0, Math.round(raw.correctChars ?? 0)),
    totalKeystrokes: Math.max(0, Math.round(raw.totalKeystrokes ?? 0)),
    backspaces: Math.max(0, Math.round(raw.backspaces ?? 0)),
    durationMs: Math.max(0, Math.round(raw.durationMs ?? 0)),
    takenAt:
      typeof raw.takenAt === 'string' && raw.takenAt
        ? raw.takenAt
        : new Date().toISOString(),
    passageId:
      typeof raw.passageId === 'string' ? raw.passageId : undefined,
    promptId: typeof raw.promptId === 'string' ? raw.promptId : undefined,
  };
}

export function saveBaseline(
  user: UserProfile,
  snapshot: BaselineSnapshot,
): UserProfile {
  if (snapshot.kind === 'transcription') {
    return {
      ...user,
      baselineTranscription: snapshot,
      baselineTranscriptionSchemaVersion: BASELINE_TRANSCRIPTION_SCHEMA,
    };
  }
  return { ...user, baselineComposition: snapshot };
}

export function compositionFluency(
  totalKeystrokes: number,
  backspaces: number,
): number {
  if (totalKeystrokes <= 0) return 1;
  return Math.max(0, (totalKeystrokes - backspaces) / totalKeystrokes);
}
